import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Upload } from 'lucide-react';
import { AsyncBoundary, Drawer, useToast } from '@kollez/design-system';
import { useGroupBilling } from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';
import { apiPost } from '@/lib/api';

/**
 * /group/billing (F88b) — direct port of kollez/pages/group/billing.html.
 *   Inkwell current-plan hero · invoices table · right column: bank details w/ copy,
 *   billing dl, adding-colleges card. Upload-payment-proof drawer.
 */
export function GroupBillingPage() {
  const { data, isLoading, error, refetch } = useGroupBilling();
  const toast = useToast();
  const [proofOpen, setProofOpen] = useState(false);
  const [utr, setUtr] = useState('');
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoiceId, setInvoiceId] = useState<string>('');

  const sub = data?.subscription ?? null;
  const invoices = data?.invoices ?? [];
  const nextRenewal = sub?.term ? formatIndianDate(sub.term.endsAt) : '—';

  async function submitProof() {
    if (!invoiceId) { toast.show({ tone: 'warning', message: 'Pick an invoice.' }); return; }
    try {
      await apiPost(`/group/invoices/${invoiceId}/payment-proof`, { UTR: utr, paidOn });
      toast.show({ message: 'Thanks. We\'ll confirm within 1 working day.' });
      setProofOpen(false);
      refetch();
    } catch { toast.show({ tone: 'warning', message: 'Could not submit proof.' }); }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Billing</h1>
          <p className="subhead">Invoiced annually against your purchase order. Pay by NEFT or RTGS; no cards are stored.</p>
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={isLoading}
        error={error}
        empty={!isLoading && !data}
        emptyTitle="No subscription found"
        emptyHint="Contact platform ops to provision your plan."
        onRetry={() => void refetch()}
      >
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
          <div className="grid gap-5 min-w-0">
            {/* Inkwell hero */}
            {sub && (
              <section className="card inkwell card-pad grid gap-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="m-0 text-sm opacity-80">Current plan</p>
                    <h2 className="h-serif text-[1.75rem] m-0">
                      {sub.plan?.name ?? 'Group'} · {sub.seats.used} of {sub.seats.allowed} colleges
                    </h2>
                    <p className="m-0 text-sm opacity-85">
                      {typeof sub.plan?.price.amount === 'number' ? `₹${sub.plan.price.amount.toLocaleString('en-IN')}` : 'Custom'} {sub.plan?.price.unit ?? ''} · {sub.billingCycle} billing
                    </p>
                  </div>
                  <span className="badge" style={{ background: 'rgb(255 255 255/.14)', color: 'inherit' }}>Renews {nextRenewal}</span>
                </div>
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">
                  <div>
                    <p className="m-0 text-xs opacity-75">Colleges</p>
                    <b className="text-lg">{sub.seats.used} of {sub.seats.allowed}</b>
                  </div>
                  <div>
                    <p className="m-0 text-xs opacity-75">AI cap</p>
                    <b className="text-lg">
                      {sub.aiCapOverride ? `₹${sub.aiCapOverride.toLocaleString('en-IN')}` : '—'}
                    </b>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="m-0 text-xs opacity-75">Discount</p>
                    <b className="text-lg">
                      {sub.discount?.percent ? `${sub.discount.percent}% off` : '—'}
                    </b>
                  </div>
                </div>
              </section>
            )}

            {/* Invoices */}
            <section className="card">
              <div className="card-head"><h2 className="h-section">Invoices</h2></div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th className="hide-mobile">Period</th>
                      <th className="r">Amount</th>
                      <th>Status</th>
                      <th><span className="sr-only">Download</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-ink-tertiary p-6">No invoices yet.</td></tr>
                    )}
                    {invoices.map((inv) => {
                      const isPaid = /paid/i.test(inv.status);
                      const isOverdue = /overdue/i.test(inv.status);
                      const isChecking = /checking|payment_proof/i.test(inv.status);
                      const tone = isPaid ? 'badge-success' : isChecking ? 'badge-info' : isOverdue ? 'badge-danger' : 'badge-warning';
                      const period = inv.period ? `${formatIndianDate(inv.period.from)} – ${formatIndianDate(inv.period.to)}` : '—';
                      return (
                        <tr key={inv._id}>
                          <td>
                            <div className="cell-title whitespace-nowrap">{inv.invoiceNumber}</div>
                            <div className="cell-sub show-mobile">{period}</div>
                          </td>
                          <td className="hide-mobile">{period}</td>
                          <td className="r">
                            <div>₹{inv.totalInr.toLocaleString('en-IN')}</div>
                            <div className="cell-sub">incl. 18% GST</div>
                          </td>
                          <td>
                            <span className={`badge ${tone}`}>
                              {isPaid ? `Paid ${inv.paidAt ? formatIndianDate(inv.paidAt) : ''}` : isOverdue ? 'Overdue' : isChecking ? 'Checking payment' : `Due ${formatIndianDate(inv.dueAt)}`}
                            </span>
                          </td>
                          <td className="r">
                            <button
                              className="btn btn-ghost btn-icon btn-sm"
                              aria-label="Download PDF"
                              disabled
                              title="Invoice PDF endpoint ships in a follow-up. Ask billing@kollez.ai for the copy today."
                            >
                              <Download size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <div className="grid gap-5 min-w-0">
            {/* Bank details */}
            <section className="card card-pad grid gap-3">
              <h2 className="h-section">Pay by bank transfer</h2>
              <dl className="dl">
                <dt>Account name</dt><dd>Kollez AI Private Limited</dd>
                <dt>Bank</dt><dd>HDFC Bank, Banjara Hills</dd>
                <dt>Account</dt>
                <dd>
                  5020 0087 1123 45{' '}
                  <button
                    className="link text-sm"
                    onClick={() => { navigator.clipboard?.writeText('50200087112345'); toast.show({ message: 'Account number copied' }); }}
                  >Copy</button>
                </dd>
                <dt>IFSC</dt><dd>HDFC0001234</dd>
              </dl>
              <p className="helper m-0">Quote the invoice number in the remarks. Payments are matched within 1 working day.</p>
              <button className="btn btn-outline" onClick={() => { setInvoiceId(invoices[0]?._id ?? ''); setProofOpen(true); }}>
                <Upload size={14} />Upload payment proof
              </button>
            </section>

            {/* Billing details */}
            <section className="card card-pad grid gap-3">
              <div className="flex items-center justify-between">
                <h2 className="h-section">Billing details</h2>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled
                  title="Billing details are edited by the group owner. Endpoint to self-serve lands in a follow-up — email billing@kollez.ai to change GST/PAN/PO."
                >
                  Edit · soon
                </button>
              </div>
              <dl className="dl">
                <dt>GSTIN</dt><dd>{sub?.billing?.gstin ?? '—'}</dd>
                <dt>PO</dt><dd>{sub?.po?.number ?? '—'}</dd>
                <dt>Send invoices to</dt><dd>{sub?.billing?.invoiceEmails?.join(', ') ?? '—'}</dd>
              </dl>
            </section>

            {/* Adding colleges */}
            <section className="card card-pad grid gap-2">
              <h2 className="h-section">Adding colleges</h2>
              <p className="helper m-0">
                {sub ? `${Math.max(0, sub.seats.allowed - sub.seats.used)} unused seats on your PO. New colleges beyond that are pro-rated to your renewal date.` : 'Contact us for seat expansion.'}
              </p>
              <Link className="btn btn-outline justify-self-start" to="/pricing">Talk to us</Link>
            </section>
          </div>
        </div>
      </AsyncBoundary>

      <Drawer open={proofOpen} onClose={() => setProofOpen(false)} title="Upload payment proof" size="md">
        <label className="grid gap-1.5 mb-4">
          <span className="label">Invoice</span>
          <select className="select" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
            {invoices.map((inv) => (
              <option key={inv._id} value={inv._id}>
                {inv.invoiceNumber} · ₹{inv.totalInr.toLocaleString('en-IN')}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1.5 mb-4">
          <span className="label">UTR / reference number</span>
          <input className="input" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. HDFCR52026091712345" />
        </label>
        <label className="grid gap-1.5 mb-4">
          <span className="label">Paid on</span>
          <input className="input" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </label>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setProofOpen(false)}>Cancel</button>
          <button className="btn btn-primary ml-auto" onClick={submitProof} disabled={!utr || !invoiceId}>Submit</button>
        </div>
      </Drawer>
    </div>
  );
}
